import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import {
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaGlobe,
  FaFacebook,
  FaTwitter,
  FaLinkedin,
  FaInstagram
} from 'react-icons/fa';
import SEO from './SEO';
import SimpleSpinner from './SimpleSpinner';
import CompanySubNav from './CompanySubNav';
import '../styles/company-profile.css';
import fallbackCompanyHero from '../pics/caliberation.png';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function CompanyProfile({ companyId: propCompanyId }) {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const params = useParams();
  const companyId = propCompanyId || params.id;

  useEffect(() => {
    let mounted = true;
    const fetchCompany = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/companies/${companyId}`, { timeout: 10000 });
        if (mounted && response.data.success) {
          setCompany(response.data.company);
          setError(null);
        }
      } catch (err) {
        if (mounted) setError('Failed to load company profile. Please try again.');
        console.error('Error fetching company:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (companyId) fetchCompany();
    return () => { mounted = false; };
  }, [companyId]);

  if (loading) {
    return (
      <div className="loading-container">
        <SimpleSpinner />
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="error-container">
        <p>{error || 'Company not found'}</p>
      </div>
    );
  }

  const getSocialIcon = (platform) => {
    const iconProps = { style: { fontSize: '1.3rem' } };
    switch (platform.toLowerCase()) {
      case 'facebook': return <FaFacebook {...iconProps} />;
      case 'twitter': return <FaTwitter {...iconProps} />;
      case 'linkedin': return <FaLinkedin {...iconProps} />;
      case 'instagram': return <FaInstagram {...iconProps} />;
      default: return null;
    }
  };

  const socialEntries = company.socialLinks ? Object.entries(company.socialLinks).filter(([_, v]) => v) : [];
  const heroBackground = company.backgroundImage || fallbackCompanyHero;

  return (
    <div className="company-profile-container">
      <SEO
        title={company.companyName}
        description={
          company.description ||
          `View ${company.companyName}'s profile, verified certifications and contact details on Dofcan Oilserve.`
        }
        path={`/company/${company._id}`}
        image={company.logo || undefined}
      />

      <CompanySubNav companyId={company._id} active="overview" />

      <div className="company-hero" style={{
        backgroundImage: `linear-gradient(135deg, rgba(0, 26, 77, 0.8) 0%, rgba(0, 61, 153, 0.8) 100%), url(${heroBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="company-hero-content">
          <div className="company-hero-text">
            <h1>{company.companyName}</h1>
            <p>{company.description}</p>
          </div>
          {company.logo && (
            <div className="company-logo-container">
              <img src={company.logo} alt={company.companyName} className="company-logo" />
            </div>
          )}
        </div>
      </div>

      <div className="company-info-section">
        <div className="info-grid">
          {company.website && (
            <div className="info-card">
              <div className="card-icon"><FaGlobe /></div>
              <h3>Website</h3>
              <a href={company.website} target="_blank" rel="noopener noreferrer">{company.website}</a>
            </div>
          )}

          {company.phone && (
            <div className="info-card">
              <div className="card-icon"><FaPhone /></div>
              <h3>Phone</h3>
              <a href={`tel:${company.phone}`}>{company.phone}</a>
            </div>
          )}

          {company.email && (
            <div className="info-card">
              <div className="card-icon"><FaEnvelope /></div>
              <h3>Email</h3>
              <a href={`mailto:${company.email}`}>{company.email}</a>
            </div>
          )}
        </div>

        <div className="company-details-grid">
          {company.foundedYear && (
            <div className="detail-item">
              <label>Founded</label>
              <p>{company.foundedYear}</p>
            </div>
          )}

          {company.industry && (
            <div className="detail-item">
              <label>Industry</label>
              <p>{company.industry}</p>
            </div>
          )}

          {company.employees && (
            <div className="detail-item">
              <label>Employees</label>
              <p>{company.employees.toLocaleString()}</p>
            </div>
          )}
        </div>

        {(company.missionStatement || company.visionStatement) && (
          <div className="statements-section">
            <h3>Mission & Vision</h3>
            <div className="statements-grid">
              {company.missionStatement && (
                <div className="statement-box">
                  <h4>Our Mission</h4>
                  <p>{company.missionStatement}</p>
                </div>
              )}
              {company.visionStatement && (
                <div className="statement-box">
                  <h4>Our Vision</h4>
                  <p>{company.visionStatement}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {company.values && company.values.length > 0 && (
          <div className="values-section">
            <h3>Core Values</h3>
            <div className="values-grid">
              {company.values.map((value, idx) => (
                <div key={idx} className="value-badge">{value}</div>
              ))}
            </div>
          </div>
        )}

        {company.address && (Object.values(company.address).some(v => v)) && (
          <div className="address-section">
            <h4><FaMapMarkerAlt style={{ marginRight: '0.5rem' }} /> Our Location</h4>
            <div className="address-text">
              {company.address.street && <p>{company.address.street}</p>}
              {company.address.city && <p>{company.address.city}, {company.address.state} {company.address.zipCode}</p>}
              {company.address.country && <p>{company.address.country}</p>}
            </div>
          </div>
        )}

        {socialEntries.length > 0 && (
          <div className="company-social-section">
            <h4 style={{ color: '#001a4d', marginBottom: '1.5rem' }}>Follow Us</h4>
            <div className="social-links">
              {socialEntries.map(([platform, url]) => (
                <a key={platform} href={url} target="_blank" rel="noopener noreferrer" className="social-link" title={platform}>
                  {getSocialIcon(platform)}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
